import { SESSION_TTL_MS, type PublicUserState, type UserStats } from '@casino/shared';

/**
 * Etat client. REGLE D'OR : le client est un rendu.
 *
 * Le solde n'est JAMAIS calcule ici. Il n'a qu'une seule source : la reponse
 * d'une route `/api/**`. Chaque reponse qui porte un `user: PublicUserState`
 * remplace integralement l'etat affiche (`adoptResponseUser`, appele par le
 * client HTTP). Aucune fonction de ce fichier n'ajoute ni ne retire un jeton.
 *
 * Ce module ne connait pas le transport : c'est le client HTTP qui l'appelle,
 * jamais l'inverse. La dependance reste a sens unique (ui -> api -> state), ce
 * qui evite un cycle d'imports et garde l'etat testable sans reseau.
 */

export type Screen = 'boot' | 'menu' | 'slots' | 'roulette' | 'lobby' | 'blackjack';

export interface AppState {
  readonly screen: Screen;
  readonly user: PublicUserState | null;
  /**
   * Epoch ms a partir duquel le bonus quotidien redevient disponible.
   * null = disponible maintenant. Derive de `nextDailyInMs` renvoye par l'API :
   * c'est un compte a rebours d'affichage, le serveur reste seul juge.
   */
  readonly dailyReadyAt: number | null;
  /**
   * Le dernier echange avec l'API a abouti : l'etat affiche est celui que le
   * serveur a renvoye. Passe a false si un rafraichissement echoue, ce que le
   * HUD signale discretement (`.hud[data-live='false']`).
   */
  readonly live: boolean;
  readonly syncError: string | null;
}

const INITIAL: AppState = {
  screen: 'boot',
  user: null,
  dailyReadyAt: null,
  live: false,
  syncError: null,
};

type Listener = (state: AppState, previous: AppState) => void;

class AppStore {
  #state: AppState = INITIAL;
  readonly #listeners = new Set<Listener>();

  get state(): AppState {
    return this.#state;
  }

  subscribe(listener: Listener, { immediate = true }: { immediate?: boolean } = {}): () => void {
    this.#listeners.add(listener);
    if (immediate) listener(this.#state, this.#state);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  patch(partial: Partial<AppState>): void {
    const previous = this.#state;
    const next: AppState = { ...previous, ...partial };
    if (shallowEqual(previous, next)) return;
    this.#state = next;
    for (const listener of [...this.#listeners]) listener(next, previous);
  }
}

function shallowEqual(a: AppState, b: AppState): boolean {
  return (
    a.screen === b.screen &&
    a.user === b.user &&
    a.dailyReadyAt === b.dailyReadyAt &&
    a.live === b.live &&
    a.syncError === b.syncError
  );
}

export const store = new AppStore();

/** Etat renvoye par l'API : autoritaire, il remplace tout. */
export function applyUserState(user: PublicUserState): void {
  store.patch({
    user,
    dailyReadyAt: user.dailyAvailable ? null : Date.now() + Math.max(0, user.nextDailyInMs),
    live: true,
    syncError: null,
  });
}

export function setScreen(screen: Screen): void {
  store.patch({ screen });
}

/** Millisecondes restantes avant le bonus, 0 s'il est disponible. */
export function dailyRemainingMs(state: AppState = store.state): number {
  if (state.dailyReadyAt === null) return 0;
  return Math.max(0, state.dailyReadyAt - Date.now());
}

// --------------------------------------------------------------------------
// Session : JWT maison, EN MEMOIRE UNIQUEMENT
// --------------------------------------------------------------------------

/**
 * Le jeton de session ne va ni dans localStorage ni dans sessionStorage :
 * une activite Discord est ephemere (l'iframe meurt avec le salon vocal) et un
 * jeton persiste ne serait qu'une surface d'attaque de plus, sans le moindre
 * confort en echange. A chaque ouverture on refait `authorize` -> `/api/token`,
 * ce qui ne coute rien quand l'utilisateur a deja autorise l'application.
 *
 * Il est volontairement hors de `AppState` : ce n'est pas de l'affichage, et
 * rien ne doit pouvoir le rendre ni le journaliser via un abonnement.
 */
let sessionToken: string | null = null;
let sessionExpiresAt = 0;

/** Marge avant expiration : on prefere re-authentifier que manger un 401. */
const EXPIRY_SKEW_MS = 30_000;

export function setSession(token: string, expiresAt: number): void {
  sessionToken = token;
  // Le serveur emet des jetons valables SESSION_TTL_MS ; une expiration plus
  // lointaine serait incoherente, on la ramene a cette borne.
  sessionExpiresAt = Math.min(expiresAt, Date.now() + SESSION_TTL_MS);
}

export function clearSession(): void {
  sessionToken = null;
  sessionExpiresAt = 0;
}

/** Le jeton courant, ou null s'il manque ou s'il est (presque) expire. */
export function getSessionToken(): string | null {
  if (sessionToken === null) return null;
  if (Date.now() >= sessionExpiresAt - EXPIRY_SKEW_MS) return null;
  return sessionToken;
}

// --------------------------------------------------------------------------
// Adoption de l'etat porte par une reponse API
// --------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isUserStats(value: unknown): value is UserStats {
  if (!isRecord(value)) return false;
  return isFiniteNumber(value['spins']) && isFiniteNumber(value['wagered']) && isFiniteNumber(value['won']);
}

/**
 * Narrowing runtime de la projection publique. Le serveur fait autorite, mais
 * une reponse tronquee ne doit pas se transformer en « solde undefined » dans
 * le HUD : on verifie la forme avant d'adopter.
 */
export function isPublicUserState(value: unknown): value is PublicUserState {
  if (!isRecord(value)) return false;
  return (
    isString(value['uid']) &&
    isString(value['username']) &&
    isNullableString(value['avatar']) &&
    isNullableString(value['avatarUrl']) &&
    isFiniteNumber(value['balance']) &&
    isFiniteNumber(value['nonce']) &&
    isString(value['clientSeed']) &&
    isString(value['serverSeedHash']) &&
    isUserStats(value['stats']) &&
    typeof value['dailyAvailable'] === 'boolean' &&
    isFiniteNumber(value['nextDailyInMs'])
  );
}

/**
 * Toute reponse `/api/**` portant un `user` met l'etat a jour. C'est LE canal
 * du solde : mise, gain, bonus, tout arrive par la reponse du serveur.
 * Renvoie true si un etat a bien ete adopte.
 */
export function adoptResponseUser(payload: unknown): boolean {
  if (!isRecord(payload)) return false;
  const user = payload['user'];
  if (!isPublicUserState(user)) return false;
  applyUserState(user);
  return true;
}

// --------------------------------------------------------------------------
// Rafraichissement au retour d'onglet
// --------------------------------------------------------------------------

/** Au plus un rafraichissement par minute : c'est un filet, pas du polling. */
const MIN_REFRESH_INTERVAL_MS = 60_000;

/**
 * Redemande l'etat autoritaire quand l'onglet redevient visible.
 *
 * Suffisant tant que le jeu est solitaire : le solde ne bouge que sur une
 * action du joueur, donc sur une reponse API. Le temps reel (tables partagees,
 * mises des autres joueurs) viendra d'un WebSocket, pas d'un polling qu'il
 * faudrait ensuite demonter.
 *
 * `refresh` est injecte pour que l'etat ne dependent pas du transport ; en
 * pratique c'est un appel a `/api/me`.
 */
export function startVisibilityRefresh(refresh: () => Promise<void>): () => void {
  // On vient de charger le profil : la fenetre d'attente demarre maintenant.
  let lastRefreshAt = Date.now();
  let inFlight = false;

  const onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;
    const now = Date.now();
    if (inFlight || now - lastRefreshAt < MIN_REFRESH_INTERVAL_MS) return;

    lastRefreshAt = now;
    inFlight = true;
    void refresh()
      .then(() => {
        store.patch({ live: true, syncError: null });
      })
      .catch((error: unknown) => {
        // Echec silencieux cote joueur : l'etat affiche reste le dernier connu,
        // le HUD signale seulement qu'il n'est plus garanti frais.
        store.patch({ live: false, syncError: error instanceof Error ? error.message : String(error) });
      })
      .finally(() => {
        inFlight = false;
      });
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
